import TimelineLite from 'gsap/TimelineLite';
import TweenLite from 'gsap/TweenLite';
import intervalTree, {
  IntervalTree,
} from 'interval-tree-1d';
import { throttle } from 'lodash-es';
import React, {
  MutableRefObject, useEffect, useRef,
} from 'react';
import styled from 'styled-components';
import {
  numFloatsPerNodeInstance,
  setCanvasSizeUniformBlock,
  setDPRUniformBlock,
  setTransformationMatrixInUniformBlock,
  updateTweenProgressInBuffer,
} from '../../network/chart/Utils';
import { convertToGLMatrix, getIdentityTransformMatrix, ITransformationMatrix} from '../../network/panZoom';
import {
  ANGLEInstancedArrays, AttributeBufferRequestType, ColorQuadruplet, ColorTriplet,
  createPopulatedGLBuffer, getProgramInfo, getUpdatePattern, GL_NUM_BYTES_PER_FLOAT,
  IAttributeRequest,
  IIndexRequest,
  IndexElementType,
  IProgramInfo,
  IUniformBlockRequest,
  PersistentFloat32Array,
  resizeViewport,
  sendAttributesToGPUWithVAO,
  sendIndicesToGPUWithVAO,
  updateGLBuffer,
  updateUniformBuffer,
  WebGLVersion,
} from '../../sharedComponents/webglUtils';
import useTrackingRef from '../../tree/newChart/useTrackingRef';
import { failIfValidOrNonExhaustive, millisecondsPerSeconds } from '../../Utils';
import usePropsChangeRateLimiter, {
  PropsChangeHandlerInput,
} from '../sectionNav/usePropsChangeRateLimiter';
import { numXTicks, numYTicks } from './appearance';
import {
  getTextLabelFragment,
} from './domDependentDrawUtils';
import {
  getIntervalTrees,
  IntervalElement,
  searchForHits,
} from './domIndependentDrawUtils';
import {
  convertToInternalLines,
  lineIndices,
  LineInternal,
  lineReferenceCoordValues,
  numFloatsPerLineInstance,
  writeToLinesBuffer,
} from './lineShadersUtils';
import {
  convertToInternalNodes,
  nodeFillIndices, nodeFillIsPointOnCircumferenceValues, nodeFillThetaValues,
  NodeInternal,
  writeToNodeBuffers as writeToNodeFillBuffers,
} from './nodeFillShadersUtils';
import { nodeStrokeIndices, nodeStrokeIsPointOnCircumferenceValues, nodeStrokeThetaValues } from './nodeStrokeUtils';
import {
  writeToBuffers as writeToNodeStrokeBuffers,
} from './nodeStrokeUtils';
import {
  ChartLine, ChartNode,
} from './otherTypes';

//#region Styling
const Root = styled.div`
  contain: content;
  position: relative;
`;
//#endregion

// We perform hit tests 30 times a second:
const hoverContinuousHitTestFrameRate = 30;

// Max number of nodes (including entering, existing and updating) that can be
// present. Because we don't change bewteen product categories, this number is
// just roughly the number of all 4-digit HS products.
const maxNumNodesAtAnyTime = 1300;

// This includes the vertical average line:
const numSpecialVerticalLines = 1;
// Max num of lines that can appear in any transition.
// Add 10 in for safety:
const maxNumLinesAtAnyTime = 2 * (numSpecialVerticalLines + numYTicks + numXTicks + 10);

const assignedUniformBlockIndex = 0;
// Number of floats contained in the uniform block:
const uniformBlockBufferSize = 16;

enum AnimationStatus {
  Initial,
  InProgress,
  Finished,
}

type Status = {
  status: AnimationStatus.Initial,
} | {
  status: AnimationStatus.InProgress;
  timeline: gsap.Timeline;
} | {
  status: AnimationStatus.Finished
  currentNodes: Map<string, NodeInternal>
  currentNodeKeys: string[]
  currentLines: Map<string, LineInternal>
  currentLineKeys: string[]

  xIntervalTree: IntervalTree<IntervalElement>
  yIntervalTree: IntervalTree<IntervalElement>

  labelsContainer: HTMLDivElement;
};

type GLInfo = {
  // Node fill-related info
  nodeFillsVAO: WebGLVertexArrayObjectOES;
  nodeFillsData: PersistentFloat32Array
  nodeFillsBuffer: WebGLBuffer
  // Node stroke related info
  nodeStrokesVAO: WebGLVertexArrayObjectOES
  nodeStrokesData: PersistentFloat32Array
  nodeStrokesBuffer: WebGLBuffer
  // Lines-related info:
  linesVAO: WebGLVertexArrayObjectOES
  linesData: PersistentFloat32Array
  linesBuffer: WebGLBuffer
} & (
  {
    version: WebGLVersion.One
    gl: WebGLRenderingContext;
    vaoExtension: OES_vertex_array_object;
    instancedDrawingExtension: ANGLEInstancedArrays
  } | {
    version: WebGLVersion.Two
    gl: WebGL2RenderingContext;
    // UBO-related info:
    uniformBlockBuffer: WebGLBuffer;
    uniformBlockData: PersistentFloat32Array;
  }
);

const getNodesAttributesRequest = (nodesBuffer: WebGLBuffer): IAttributeRequest[] => {
  // This is the number of bytes that the instanced attributes use per vertex:
  const strideForInstancedAttributes = numFloatsPerNodeInstance * GL_NUM_BYTES_PER_FLOAT;
  return [
    {
      name: 'isPointOnCircumference',
      numFloatsPerVertex: 1,
      buffer: {
        type: AttributeBufferRequestType.Implicit,
        totalSizeAsNumOfFloats: nodeFillIsPointOnCircumferenceValues.length,
      },
      stride: 0, offset: 0,
      isInstanced: false,
    }, {
      name: 'theta',
      numFloatsPerVertex: 1,
      buffer: {
        type: AttributeBufferRequestType.Implicit,
        totalSizeAsNumOfFloats: nodeFillThetaValues.length,
      },
      stride: 0, offset: 0,
      isInstanced: false,
    }, {
      name: 'initialCenter',
      numFloatsPerVertex: 2,
      buffer: {
        type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer,
      },
      offset: 0,
      stride: strideForInstancedAttributes,
      isInstanced: true,
    }, {
      name: 'finalCenter',
      numFloatsPerVertex: 2,
      buffer: {
        type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer,
      },
      offset: 2 * GL_NUM_BYTES_PER_FLOAT,
      stride: strideForInstancedAttributes,
      isInstanced: true,
    }, {
      name: 'initialRadius',
      numFloatsPerVertex: 1,
      buffer: {type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer},
      stride: strideForInstancedAttributes,
      offset: 4 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'finalRadius',
      numFloatsPerVertex: 1,
      buffer: {type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer},
      stride: strideForInstancedAttributes,
      offset: 5 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'initialHalfStrokeWidth',
      numFloatsPerVertex: 1,
      buffer: {type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer},
      stride: strideForInstancedAttributes,
      offset: 6 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'finalHalfStrokeWidth',
      numFloatsPerVertex: 1,
      buffer: {type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer},
      stride: strideForInstancedAttributes,
      offset: 7 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'initialColor',
      numFloatsPerVertex: 4,
      buffer: {type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer},
      stride: strideForInstancedAttributes,
      offset: 8 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'finalColor',
      numFloatsPerVertex: 4,
      buffer: {type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer},
      stride: strideForInstancedAttributes,
      offset: 12 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'strokeAdjustmentFactor',
      numFloatsPerVertex: 1,
      buffer: {type: AttributeBufferRequestType.Explicit, buffer: nodesBuffer},
      stride: strideForInstancedAttributes,
      offset: 16 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    },
  ];
};

const getLinesAttributesRequest = (linesBuffer: WebGLBuffer): IAttributeRequest[] => {
  const strideForInstancedAttributes = numFloatsPerLineInstance * GL_NUM_BYTES_PER_FLOAT;
  return [
    {
      name: 'referenceCoord',
      numFloatsPerVertex: 2,
      buffer: {
        type: AttributeBufferRequestType.Implicit,
        totalSizeAsNumOfFloats: lineReferenceCoordValues.length,
      },
      stride: 0, offset: 0,
      isInstanced: false,
    }, {
      name: 'initialColor',
      numFloatsPerVertex: 4,
      buffer: {
        type: AttributeBufferRequestType.Explicit,
        buffer: linesBuffer,
      },
      stride: strideForInstancedAttributes,
      offset: 0 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'finalColor',
      numFloatsPerVertex: 4,
      buffer: {
        type: AttributeBufferRequestType.Explicit,
        buffer: linesBuffer,
      },
      stride: strideForInstancedAttributes,
      offset: 4 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'lineStart',
      numFloatsPerVertex: 2,
      buffer: {
        type: AttributeBufferRequestType.Explicit,
        buffer: linesBuffer,
      },
      stride: strideForInstancedAttributes,
      offset: 8 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'lineEnd',
      numFloatsPerVertex: 2,
      buffer: {
        type: AttributeBufferRequestType.Explicit,
        buffer: linesBuffer,
      },
      stride: strideForInstancedAttributes,
      offset: 10 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'thickness',
      numFloatsPerVertex: 1,
      buffer: {
        type: AttributeBufferRequestType.Explicit, buffer: linesBuffer,
      },
      stride: strideForInstancedAttributes,
      offset: 12 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    }, {
      name: 'dashedLineSolidSegmentLength',
      numFloatsPerVertex: 1,
      buffer: {
        type: AttributeBufferRequestType.Explicit,
        buffer: linesBuffer,
      },
      stride: strideForInstancedAttributes,
      offset: 13 * GL_NUM_BYTES_PER_FLOAT,
      isInstanced: true,
    },
  ];
};

const setupWebGL = (inputs: {
    canvasRef: MutableRefObject<HTMLCanvasElement | null>,
    nodeFillProgramRef: MutableRefObject<IProgramInfo | undefined>,
    nodeStrokeProgramRef: MutableRefObject<IProgramInfo | undefined>,
    lineProgramRef: MutableRefObject<IProgramInfo | undefined>,
    glInfoRef: MutableRefObject<GLInfo | undefined>,
  }) => {

    const {
      canvasRef: {current: canvas},
      nodeFillProgramRef, glInfoRef, lineProgramRef,
      nodeStrokeProgramRef,
    } = inputs;

    if (canvas !== null) {
      const nodeFillsData = new PersistentFloat32Array(
        maxNumNodesAtAnyTime * numFloatsPerNodeInstance,
      );
      const nodeFillsIndexRequest: IIndexRequest = {
        elementType: IndexElementType.Int16,
        totalSizeAsNumOfInts: nodeFillIndices.length,
      };
      const nodeStrokesData = new PersistentFloat32Array(maxNumNodesAtAnyTime * numFloatsPerNodeInstance);
      const nodeStrokesIndexRequest: IIndexRequest = {
        elementType: IndexElementType.Int16,
        totalSizeAsNumOfInts: nodeStrokeIndices.length,
      };
      const linesData = new PersistentFloat32Array(maxNumLinesAtAnyTime * numFloatsPerLineInstance);
      const linesIndexRequest: IIndexRequest = {
        elementType: IndexElementType.Int16, totalSizeAsNumOfInts: lineIndices.length,
      };
      const gl2 = canvas.getContext('webgl2', {alpha: false});
      if (gl2 !== null) {
        const nodeFillsVAO = gl2.createVertexArray();
        if (nodeFillsVAO === null) {
          throw new Error('Unable to create VAO for nodes');
        }
        const uniformBlockData = new PersistentFloat32Array(uniformBlockBufferSize);
        const {buffer: uniformBlockBuffer} = createPopulatedGLBuffer(gl2, uniformBlockData);
        const sharedUniformBlockRequest: IUniformBlockRequest = {
          name: 'uniformBlock',
          assignedBlockIndex: assignedUniformBlockIndex,
          assignedBuffer: uniformBlockBuffer,
        };

        const {buffer: nodeFillsBuffer} = createPopulatedGLBuffer(gl2, nodeFillsData);
        nodeFillProgramRef.current = getProgramInfo({
          version: WebGLVersion.Two,
          gl: gl2,
          vertexShader: require('../../network/chart/2Node.vert'),
          fragmentShader: require('../../network/chart/2SimpleFourColorPassThrough.frag'),
          attributes: getNodesAttributesRequest(nodeFillsBuffer),
          indexBuffer: nodeFillsIndexRequest,
          vaoObject: nodeFillsVAO,
          uniforms: [],
          uniformBlocks: [sharedUniformBlockRequest],
        });

        const nodeStrokesVAO = gl2.createVertexArray();
        if (nodeStrokesVAO === null) {
          throw new Error('Unable to create VAO for nodes');
        }
        const {buffer: nodeStrokesBuffer} = createPopulatedGLBuffer(gl2, nodeStrokesData);
        nodeStrokeProgramRef.current = getProgramInfo({
          version: WebGLVersion.Two,
          gl: gl2,
          vertexShader: require('../../network/chart/2Node.vert'),
          fragmentShader: require('../../network/chart/2SimpleFourColorPassThrough.frag'),
          attributes: getNodesAttributesRequest(nodeStrokesBuffer),
          indexBuffer: nodeStrokesIndexRequest,
          vaoObject: nodeStrokesVAO,
          uniforms: [],
          uniformBlocks: [sharedUniformBlockRequest],
        });

        const linesVAO = gl2.createVertexArray();
        if (linesVAO === null) {
          throw new Error('Unable to create VAO for lines');
        }
        const {buffer: linesBuffer} = createPopulatedGLBuffer(gl2, linesData);

        lineProgramRef.current = getProgramInfo({
          version: WebGLVersion.Two,
          gl: gl2,
          vertexShader: require('./2Line.vert'),
          fragmentShader: require('../../network/chart/2Edge.frag'),
          attributes: getLinesAttributesRequest(linesBuffer),
          indexBuffer: linesIndexRequest,
          vaoObject: linesVAO,
          uniforms: [],
          uniformBlocks: [sharedUniformBlockRequest],
        });

        glInfoRef.current = {
          version: WebGLVersion.Two,
          gl: gl2,
          nodeFillsVAO, nodeFillsData, nodeFillsBuffer,
          nodeStrokesVAO, nodeStrokesData, nodeStrokesBuffer,
          linesVAO, linesData, linesBuffer,
          uniformBlockBuffer, uniformBlockData,
        };
      } else {
        const gl1 = canvas.getContext('webgl', {alpha: false});
        if (gl1 === null) {
          console.warn('WebGL is not available');
          glInfoRef.current = undefined;
        } else {
          const vaoExtension = gl1.getExtension('OES_vertex_array_object');
          const instancedDrawingExtension = gl1.getExtension('ANGLE_instanced_arrays');

          if (!vaoExtension || !instancedDrawingExtension) {
            if (!vaoExtension) {
              console.warn('OES_vertex_array_object extension not available.');
            }
            if (!instancedDrawingExtension) {
              console.warn('ANGLE_instanced_arrays extension not available');
            }
            glInfoRef.current = undefined;
          } else {
            const nodeFillsVAO = vaoExtension.createVertexArrayOES();
            if (nodeFillsVAO === null) {
              throw new Error('Unable to create VAO for nodes');
            }
            const {buffer: nodeFillsBuffer} = createPopulatedGLBuffer(gl1, nodeFillsData);
            nodeFillProgramRef.current = getProgramInfo({
              version: WebGLVersion.One,
              gl: gl1,
              vertexShader: require('../../network/chart/node.vert'),
              fragmentShader: require('../../network/chart/simpleFourColorPassThrough.frag'),
              attributes: getNodesAttributesRequest(nodeFillsBuffer),
              indexBuffer: nodeFillsIndexRequest,
              vaoObject: nodeFillsVAO,
              uniforms: ['transform', 'canvasSize', 'tweenProgress'],
              vaoExtension,
              instancedDrawingExtension,
            });

            const nodeStrokesVAO = vaoExtension.createVertexArrayOES();
            if (nodeStrokesVAO === null) {
              throw new Error('Unable to create VAO for nodes');
            }
            const {buffer: nodeStrokesBuffer} = createPopulatedGLBuffer(gl1, nodeStrokesData);
            nodeStrokeProgramRef.current = getProgramInfo({
              version: WebGLVersion.One,
              gl: gl1,
              vertexShader: require('../../network/chart/node.vert'),
              fragmentShader: require('../../network/chart/simpleFourColorPassThrough.frag'),
              attributes: getNodesAttributesRequest(nodeStrokesBuffer),
              indexBuffer: nodeStrokesIndexRequest,
              vaoObject: nodeStrokesVAO,
              uniforms: ['transform', 'canvasSize', 'tweenProgress'],
              vaoExtension,
              instancedDrawingExtension,
            });

            const linesVAO = vaoExtension.createVertexArrayOES();
            if (linesVAO === null) {
              throw new Error('Unable to create VAO for lines');
            }
            const {buffer: linesBuffer} = createPopulatedGLBuffer(gl1, linesData);

            lineProgramRef.current = getProgramInfo({
              version: WebGLVersion.One,
              gl: gl1,
              vertexShader: require('./line.vert'),
              fragmentShader: require('../../network/chart/edge.frag'),
              attributes: getLinesAttributesRequest(linesBuffer),
              indexBuffer: linesIndexRequest,
              vaoObject: linesVAO,
              uniforms: ['transform', 'canvasSize', 'tweenProgress', 'devicePixelRatio'],
              vaoExtension,
              instancedDrawingExtension,
            });

            glInfoRef.current = {
              version: WebGLVersion.One,
              gl: gl1,
              nodeFillsVAO, nodeFillsData, nodeFillsBuffer,
              nodeStrokesVAO, nodeStrokesData, nodeStrokesBuffer,
              linesVAO, linesData, linesBuffer,
              instancedDrawingExtension, vaoExtension,
            };
          }
        }
      }
    }
  };

const haveNodesChanged = (prevNodes: ChartNode[], nextNodes: ChartNode[]): boolean => {
  const prevLength = prevNodes.length;
  const nextLength = nextNodes.length;
  if (prevLength !== nextLength) {
    return true;
  } else {
    for (let i = 0; i < prevLength; i += 1) {
      const prevNode = prevNodes[i];
      const nextNode = nextNodes[i];
      if (prevNode.id !== nextNode.id || prevNode.x !== nextNode.x ||
            prevNode.y !== nextNode.y || prevNode.radius !== nextNode.radius ||
            prevNode.color !== nextNode.color) {
        return true;
      }
    }
    return false;
  }
};

const haveLinesChanged = (prevLines: ChartLine[], nextLines: ChartLine[]): boolean => {
  const prevLength = prevLines.length;
  const nextLength = nextLines.length;
  if (prevLength !== nextLength) {
    return true;
  } else {
    for (let i = 0; i < prevLength; i += 1) {
      const prevLine = prevLines[i];
      const nextLine = nextLines[i];
      if (prevLine.id !== nextLine.id || prevLine.x0 !== nextLine.x0 ||
          prevLine.y0 !== nextLine.y0 || prevLine.x1 !== nextLine.y1 ||
          prevLine.y1 !== nextLine.y1 || prevLine.style !== nextLine.style) {
        return true;
      }
    }
    return false;
  }
};

interface PropsChangeHandlerExtraInputs {
  glInfoRef: MutableRefObject<GLInfo | undefined>;
  nodeFillProgramRef: MutableRefObject<IProgramInfo | undefined>;
  nodeStrokeProgramRef: MutableRefObject<IProgramInfo | undefined>;
  lineProgramRef: MutableRefObject<IProgramInfo | undefined>;
  statusRef: MutableRefObject<Status>;
  transformationMatrixRef: MutableRefObject<ITransformationMatrix>;
  dataSetTransitionDuration: number;
  hoverTransitionDuration: number;
  shortDashLength: number;
  longDashLength: number;
  lineColor: ColorQuadruplet;
  unhoveredNodeColor: ColorTriplet;
  rootElRef: MutableRefObject<HTMLDivElement | null>;
  maxNumLabels: number;
  longDashColor: ColorQuadruplet;
}

type MonitoredProps = Pick<
  Props, 'nodes' | 'width' | 'height' | 'lines' | 'hoveredNode'
>;

const performPropsChange =
  (input: PropsChangeHandlerInput<PropsChangeHandlerExtraInputs, MonitoredProps>) => {

  const {
    nextValue,
    extraInputs: {
      glInfoRef: {current: glInfo},
      nodeFillProgramRef: {current: nodeFillProgram},
      nodeStrokeProgramRef: {current: nodeStrokeProgram},
      lineProgramRef: {current: lineProgram},
      statusRef: {current: prevStatus}, statusRef,
      transformationMatrixRef: {current: transformationMatrix},
      dataSetTransitionDuration, hoverTransitionDuration,
      shortDashLength, longDashLength, lineColor,
      unhoveredNodeColor, longDashColor,
      rootElRef: {current: rootEl},
      maxNumLabels,
    },
    done,
  } = input;

  let prevValue: MonitoredProps, hasChartSizeChanged: boolean;
  if (input.prevValue === undefined) {
    // This means this is the very first props change so assign all nodes a very
    // small size so that they can animate "growing" into their full sizes:
    const prevNodes = nextValue.nodes.map(elem => ({...elem, radius: 1}));
    prevValue = {
      nodes: prevNodes,
      width: nextValue.width, height: nextValue.height,
      lines: nextValue.lines,
      hoveredNode: undefined,
    };
    // If this is the very first props change, assume that chart size has changed to trigger
    // render:
    hasChartSizeChanged = true;
  } else {
    prevValue = input.prevValue;
    hasChartSizeChanged = prevValue.width !== nextValue.width || prevValue.height !== nextValue.height;
  }

  if (hasChartSizeChanged && glInfo !== undefined) {
    const {gl} = glInfo;
    resizeViewport(gl, true);
  }

  if (glInfo !== undefined && nodeFillProgram !== undefined &&
      nodeStrokeProgram !== undefined && lineProgram !== undefined && rootEl !== null &&
      (prevStatus.status === AnimationStatus.Finished || prevStatus.status === AnimationStatus.Initial)) {

    const nodesHaveChanged = haveNodesChanged(prevValue.nodes, nextValue.nodes);
    const hoveredNodeHasChanged = (prevValue.hoveredNode !== nextValue.hoveredNode);
    if (hasChartSizeChanged || nodesHaveChanged ||
        haveLinesChanged(prevValue.lines, nextValue.lines) || hoveredNodeHasChanged) {

      const {
        nodeFillsVAO, nodeStrokesVAO,
        linesVAO,
        gl, nodeFillsData, nodeFillsBuffer,
        nodeStrokesData, nodeStrokesBuffer,
        linesData, linesBuffer,
      } = glInfo;

      let prevNodes: Map<string, NodeInternal>, prevNodeKeys: string[];
      let prevLines: Map<string, LineInternal>, prevLineKeys: string[];
      if (prevStatus.status === AnimationStatus.Finished) {
        prevNodes = prevStatus.currentNodes;
        prevNodeKeys = prevStatus.currentNodeKeys;
        prevLines = prevStatus.currentLines;
        prevLineKeys = prevStatus.currentLineKeys;
      } else if (prevStatus.status === AnimationStatus.Initial) {
        prevNodes = new Map();
        prevNodeKeys = [];
        prevLines = new Map();
        prevLineKeys = [];

        if (glInfo.version === WebGLVersion.Two) {
          glInfo.gl.bindVertexArray(nodeFillsVAO);
        } else {
          glInfo.vaoExtension.bindVertexArrayOES(nodeFillsVAO);
        }

        gl.useProgram(nodeFillProgram.program);

        // Send values of non-instanced attributes to the GPU:
        sendAttributesToGPUWithVAO({
          gl, programInfo: nodeFillProgram,
          attributeName: 'isPointOnCircumference',
          data: new Float32Array(nodeFillIsPointOnCircumferenceValues),
        });
        sendAttributesToGPUWithVAO({
          gl, programInfo: nodeFillProgram,
          attributeName: 'theta',
          data: new Float32Array(nodeFillThetaValues),
        });
        sendIndicesToGPUWithVAO({
          gl, programInfo: nodeFillProgram, data: new Uint16Array(nodeFillIndices),
        });

        if (glInfo.version === WebGLVersion.Two) {
          glInfo.gl.bindVertexArray(nodeStrokesVAO);
        } else {
          glInfo.vaoExtension.bindVertexArrayOES(nodeStrokesVAO);
        }
        gl.useProgram(nodeStrokeProgram.program);
        sendAttributesToGPUWithVAO({
          gl, programInfo: nodeStrokeProgram,
          attributeName: 'isPointOnCircumference',
          data: new Float32Array(nodeStrokeIsPointOnCircumferenceValues),
        });
        sendAttributesToGPUWithVAO({
          gl, programInfo: nodeStrokeProgram,
          attributeName: 'theta',
          data: new Float32Array(nodeStrokeThetaValues),
        });
        sendIndicesToGPUWithVAO({
          gl, programInfo: nodeStrokeProgram,
          data: new Uint16Array(nodeStrokeIndices),
        });

        if (glInfo.version === WebGLVersion.Two) {
          glInfo.gl.bindVertexArray(linesVAO);
        } else {
          glInfo.vaoExtension.bindVertexArrayOES(linesVAO);
        }
        gl.useProgram(lineProgram.program);

        sendAttributesToGPUWithVAO({
          gl, programInfo: lineProgram,
          attributeName: 'referenceCoord',
          data: new Float32Array(lineReferenceCoordValues),
        });
        sendIndicesToGPUWithVAO({
          gl, programInfo: lineProgram, data: new Uint16Array(lineIndices),
        });

        if (glInfo.version === WebGLVersion.Two) {
          glInfo.gl.bindVertexArray(null as any);
        } else {
          glInfo.vaoExtension.bindVertexArrayOES(null);
        }

        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.clearColor(255 / 255, 255 / 255, 255 / 255, 1);
      } else {
        failIfValidOrNonExhaustive(prevStatus, 'Invalid status ' + prevStatus);
        // These lines will never be executed:
        prevNodes = new Map();
        prevNodeKeys = [];
        prevLines = new Map();
        prevLineKeys = [];
      }

      const doesNextUpdateHaveData = nextValue.nodes.length > 0;
      if (doesNextUpdateHaveData === true) {
        const {nextNodes, nextKeys: nextNodeKeys} = convertToInternalNodes({
          nodes: nextValue.nodes,
          hoveredNode: nextValue.hoveredNode,
          unhoveredNodeColor,
        });
        const nodesUpdatePattern = getUpdatePattern(prevNodeKeys, nextNodeKeys);
        const nodeFillInfo = writeToNodeFillBuffers({
          prevNodes, nextNodes, updatePattern: nodesUpdatePattern, data: nodeFillsData,
          isFirstRender: (input.prevValue === undefined),
        });
        updateGLBuffer({gl, data: nodeFillsData.getMeaningfulData(), buffer: nodeFillsBuffer});

        const nodeStrokeInfo = writeToNodeStrokeBuffers({
          prevNodes, nextNodes, updatePattern: nodesUpdatePattern,
          data: nodeStrokesData,
          isFirstRender: (input.prevValue === undefined),
        });
        updateGLBuffer({
          gl, data: nodeStrokesData.getMeaningfulData(), buffer: nodeStrokesBuffer,
        });

        const {nextLines, nextKeys: nextLineKeys} = convertToInternalLines({
          lines: nextValue.lines,
          shortDashLength,
          longDashLength,
          canvasLargerSide: (nextValue.width > nextValue.height) ? nextValue.width : nextValue.height,
          standardColor: lineColor,
          longDashColor,

        });
        const linesUpdatePattern = getUpdatePattern(prevLineKeys, nextLineKeys);
        const linesInfo = writeToLinesBuffer({
          prevLines, nextLines, linesData, updatePattern: linesUpdatePattern,
        });
        updateGLBuffer({gl, data: linesData.getMeaningfulData(), buffer: linesBuffer});

        let nextXIntervalTree: IntervalTree<IntervalElement>;
        let nextYIntervalTree: IntervalTree<IntervalElement>;
        if ((doesNextUpdateHaveData && haveNodesChanged(prevValue.nodes, nextValue.nodes)) ||
            prevStatus.status === AnimationStatus.Initial) {

          const intervalTrees = getIntervalTrees(nextNodeKeys, nextNodes);
          nextXIntervalTree = intervalTrees.xIntervalTree;
          nextYIntervalTree = intervalTrees.yIntervalTree;
        } else {
          nextXIntervalTree = prevStatus.xIntervalTree;
          nextYIntervalTree = prevStatus.yIntervalTree;
        }
        const onAnimationStart = (
            inputTransformationMatrix: ITransformationMatrix,
            inputWidth: number,
            inputHeight: number,
          ) => {

          if (glInfo.version === WebGLVersion.Two) {
            const uniformBlockData = glInfo.uniformBlockData;
            const uniformBlockBuffer = glInfo.uniformBlockBuffer;

            setTransformationMatrixInUniformBlock(uniformBlockData, inputTransformationMatrix);
            setCanvasSizeUniformBlock(uniformBlockData, inputWidth, inputHeight);
            setDPRUniformBlock(uniformBlockData, devicePixelRatio);
            updateUniformBuffer({gl: glInfo.gl, data: uniformBlockData.buffer, buffer: uniformBlockBuffer});
            glInfo.gl.bindBuffer(glInfo.gl.UNIFORM_BUFFER, uniformBlockBuffer);
          } else {
            const glMatrix = convertToGLMatrix(inputTransformationMatrix);

            gl.useProgram(nodeFillProgram.program);
            gl.uniform2f(nodeFillProgram.uniforms.canvasSize, inputWidth, inputHeight);
            gl.uniformMatrix3fv(nodeFillProgram.uniforms.transform, false, glMatrix);

            gl.useProgram(nodeStrokeProgram.program);
            gl.uniform2f(nodeStrokeProgram.uniforms.canvasSize, inputWidth, inputHeight);
            gl.uniformMatrix3fv(nodeStrokeProgram.uniforms.transform, false, glMatrix);

            gl.useProgram(lineProgram.program);
            gl.uniform2f(lineProgram.uniforms.canvasSize, inputWidth, inputHeight);
            gl.uniformMatrix3fv(lineProgram.uniforms.transform, false, glMatrix);
            gl.uniform1f(lineProgram.uniforms.devicePixelRatio, window.devicePixelRatio);
          }
        };

        const tweenTarget = {tweenProgress: 0};
        const timeline = new TimelineLite({paused: true});

        type LabelsContainerStatus = {
          isLabelsContainerChanging: true
          prevContainer: HTMLDivElement
          nextContainer: HTMLDivElement
          fragment: DocumentFragment,
        } | {
          isLabelsContainerChanging: false
          currentContainer: HTMLDivElement,
        };

        let labelsContainerStatus: LabelsContainerStatus;
        if (nodesHaveChanged === true || prevStatus.status === AnimationStatus.Initial) {
          const {fragment, rootNode} = getTextLabelFragment(
            nextNodes, nodesUpdatePattern, maxNumLabels,
          );
          const prevContainer = (prevStatus.status === AnimationStatus.Initial) ?
                                  document.createElement('div') :
                                  prevStatus.labelsContainer;
          labelsContainerStatus = {
            isLabelsContainerChanging: true,
            prevContainer,
            nextContainer: rootNode,
            fragment,
          };
        } else {
          labelsContainerStatus = {
            isLabelsContainerChanging: false,
            currentContainer: prevStatus.labelsContainer,
          };
        }

        if (labelsContainerStatus.isLabelsContainerChanging === true) {
          timeline.set(
            labelsContainerStatus.nextContainer,
            {css: {opacity: 1}},
            dataSetTransitionDuration / millisecondsPerSeconds / 2,
          );
          timeline.set(labelsContainerStatus.prevContainer, {css: {opacity: 0}}, 0);
        }

        const onAnimationUpdate = () => {
          const tweenProgress = tweenTarget.tweenProgress;
          // tslint:disable-next-line:no-bitwise
          gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
          if (glInfo.version === WebGLVersion.Two) {
            const gl2 = glInfo.gl;
            updateTweenProgressInBuffer(gl2, tweenProgress);

            gl2.useProgram(lineProgram.program);
            gl2.bindVertexArray(linesVAO);
            gl2.drawElementsInstanced(
              gl2.TRIANGLES, lineIndices.length, lineProgram.indexBuffer.elementType,
              0, linesInfo.instanceCount,
            );

            gl2.useProgram(nodeFillProgram.program);
            gl2.bindVertexArray(nodeFillsVAO);
            gl2.drawElementsInstanced(
              gl2.TRIANGLES, nodeFillIndices.length, nodeFillProgram.indexBuffer.elementType,
              0, nodeFillInfo.instanceCount,
            );

            gl2.useProgram(nodeStrokeProgram.program);
            gl2.bindVertexArray(nodeStrokesVAO);
            gl2.drawElementsInstanced(
              gl2.LINES, nodeStrokeIndices.length, nodeStrokeProgram.indexBuffer.elementType,
              0, nodeStrokeInfo.instanceCount,
            );
          } else {
            const gl1 = glInfo.gl;
            const {instancedDrawingExtension, vaoExtension}  = glInfo;

            gl1.useProgram(lineProgram.program);
            gl.uniform1f(lineProgram.uniforms.tweenProgress, tweenProgress);
            vaoExtension.bindVertexArrayOES(linesVAO);
            instancedDrawingExtension.drawElementsInstancedANGLE(
              gl1.TRIANGLES, lineIndices.length, lineProgram.indexBuffer.elementType,
              0, linesInfo.instanceCount,
            );

            gl1.useProgram(nodeFillProgram.program);
            gl.uniform1f(nodeFillProgram.uniforms.tweenProgress, tweenProgress);
            vaoExtension.bindVertexArrayOES(nodeFillsVAO);
            instancedDrawingExtension.drawElementsInstancedANGLE(
              gl1.TRIANGLES, nodeFillIndices.length, nodeFillProgram.indexBuffer.elementType,
              0, nodeFillInfo.instanceCount,
            );

            gl1.useProgram(nodeStrokeProgram.program);
            gl.uniform1f(nodeStrokeProgram.uniforms.tweenProgress, tweenProgress);
            vaoExtension.bindVertexArrayOES(nodeStrokesVAO);
            instancedDrawingExtension.drawElementsInstancedANGLE(
              gl1.LINES, nodeStrokeIndices.length, nodeStrokeProgram.indexBuffer.elementType,
              0, nodeStrokeInfo.instanceCount,
            );
          }
        };

        const onAnimationComplete = () => {
          const nextLabelsContainer = (labelsContainerStatus.isLabelsContainerChanging === true) ?
                                    labelsContainerStatus.nextContainer :
                                    labelsContainerStatus.currentContainer;
          const endPropsChangeTransition = () => {
            statusRef.current = {
              status: AnimationStatus.Finished,
              currentNodes: nextNodes,
              currentNodeKeys: nextNodeKeys,
              currentLines: nextLines,
              currentLineKeys: nextLineKeys,
              xIntervalTree: nextXIntervalTree,
              yIntervalTree: nextYIntervalTree,
              labelsContainer: nextLabelsContainer,
            };
            done();
          };
          if (glInfo.version === WebGLVersion.Two) {
            glInfo.gl.bindVertexArray(null as any);
          } else {
            glInfo.vaoExtension.bindVertexArrayOES(null);
          }
          if (labelsContainerStatus.isLabelsContainerChanging === true) {
            const toBeRemoved = labelsContainerStatus.prevContainer;
            requestAnimationFrame(() => {
              toBeRemoved.remove();
              endPropsChangeTransition();
            });
          } else {
            endPropsChangeTransition();
          }
        };
        const transitionDuration = (nodesHaveChanged === false && hoveredNodeHasChanged === true) ?
                                      hoverTransitionDuration : dataSetTransitionDuration;
        const animationTween = TweenLite.to(tweenTarget, transitionDuration / millisecondsPerSeconds, {
          tweenProgress: 1,
          ease: 'Cubic.easeOut',
          onUpdate: onAnimationUpdate,
          onComplete: onAnimationComplete,
        });
        timeline.add(animationTween, 0);
        onAnimationStart(transformationMatrix, nextValue.width, nextValue.height);
        statusRef.current = {
          status: AnimationStatus.InProgress,
          timeline,
        };
        if (labelsContainerStatus.isLabelsContainerChanging === true) {
          const toBeAppended = labelsContainerStatus.nextContainer;
          requestAnimationFrame(() => {
            rootEl.appendChild(toBeAppended);
            requestAnimationFrame(() => {
              timeline.play();
            });
          });
        } else {
          timeline.play();
        }
      } else {
        gl.clearColor(255 / 255, 255 / 255, 255 / 255, 1);
        // tslint:disable-next-line:no-bitwise
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        statusRef.current = {
          status: AnimationStatus.Finished,
          currentNodes: new Map(),
          currentNodeKeys: [],
          currentLines: new Map(),
          currentLineKeys: [],
          xIntervalTree: intervalTree([]),
          yIntervalTree: intervalTree([]),
          labelsContainer: document.createElement('div'),
        };
        done();
      }
    } else {
      done();
    }
  } else {
    done();
  }
};

const performHitTest = (input: {
    canvas: HTMLCanvasElement
    status: Status,
    width: number,
    height: number,
    transformationMatrix: ITransformationMatrix,
    clientX: number,
    clientY: number,
  }): undefined | string => {

  const {
    canvas, status, width, height, transformationMatrix,
    clientX, clientY,
  } = input;

  if (canvas !== null && status.status === AnimationStatus.Finished) {
    const {xIntervalTree, yIntervalTree} = status;
    const {top, left} = canvas.getBoundingClientRect();
    const relativeX = clientX - left;
    const relativeY = clientY - top;
    return searchForHits(
      xIntervalTree, yIntervalTree, width, height, relativeX, relativeY, transformationMatrix,
    );
  } else {
    return undefined;
  }
};

const getUnthrottledHoverHandler = (input: {
    canvasRef: MutableRefObject<HTMLCanvasElement | null>,
    statusRef: MutableRefObject<Status>,
    widthRef: MutableRefObject<number>,
    heightRef: MutableRefObject<number>,
    transformationMatrixRef: MutableRefObject<ITransformationMatrix>,
    onNodeMouseEnterRef: MutableRefObject<Props['onNodeMouseEnter']>,
    onNodeMouseLeaveRef: MutableRefObject<Props['onNodeMouseLeave']>,
    // Note: need to update these refs every time before invoking the throttled
    // version of `performHover`:
    mouseClientXRef: MutableRefObject<number | undefined>,
    mouseClientYRef: MutableRefObject<number | undefined>,
  }) => {

  const {
    mouseClientXRef, mouseClientYRef,
    statusRef, canvasRef, widthRef, heightRef,
    transformationMatrixRef,
    onNodeMouseEnterRef, onNodeMouseLeaveRef,
  } = input;

  let hoveredNode: string | undefined;

  return () => {
    const {current: status} = statusRef;
    const {current: clientX} = mouseClientXRef;
    const {current: clientY} = mouseClientYRef;
    const {current: canvas} = canvasRef;
    const {current: width} = widthRef;
    const {current: height} = heightRef;
    const {current: transformationMatrix} = transformationMatrixRef;
    const {current: onNodeMouseEnter} = onNodeMouseEnterRef;
    const {current: onNodeMouseLeave} = onNodeMouseLeaveRef;
    if (status.status === AnimationStatus.Finished && canvas !== null &&
          clientX !== undefined && clientY !== undefined) {
      const prevHoveredNode = hoveredNode;
      const nextHoveredNode = performHitTest({
        canvas, status, width, height,
        transformationMatrix,
        clientX, clientY,
      });
      hoveredNode = nextHoveredNode;
      if (nextHoveredNode !== prevHoveredNode) {
        if (prevHoveredNode === undefined && nextHoveredNode !== undefined) {
          onNodeMouseEnter(nextHoveredNode);
        } else if (prevHoveredNode !== undefined && nextHoveredNode === undefined) {
          onNodeMouseLeave();
        } else if (prevHoveredNode !== undefined && nextHoveredNode !== undefined) {
          onNodeMouseEnter(nextHoveredNode);
        }
      }

    }
  };
};

interface Props {
  nodes: ChartNode[];
  lines: ChartLine[];
  hoveredNode: string | undefined;
  width: number;
  height: number;
  shortDashLength: number;
  longDashLength: number;
  lineColor: ColorQuadruplet;
  dataSetTransitionDuration: number;
  hoverTransitionDuration: number;
  unhoveredNodeColor: ColorTriplet;
  onNodeMouseEnter: (id: string) => void;
  onNodeMouseLeave: () => void;
  maxNumLabels: number;
  longDashColor: ColorQuadruplet;
}

export default (props: Props) => {
  const rootElRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const statusRef = useRef<Status>({status: AnimationStatus.Initial});

  const glInfoRef = useRef<GLInfo | undefined>(undefined);
  const nodeFillProgramRef = useRef<IProgramInfo | undefined>(undefined);
  const nodeStrokeProgramRef = useRef<IProgramInfo | undefined>(undefined);
  const lineProgramRef = useRef<IProgramInfo | undefined>(undefined);

  const transformationMatrixRef = useRef<ITransformationMatrix>(getIdentityTransformMatrix());

  const widthRef = useTrackingRef(props.width);
  const heightRef = useTrackingRef(props.height);
  const onNodeMouseEnterRef = useTrackingRef(props.onNodeMouseEnter);
  const onNodeMouseLeaveRef = useTrackingRef(props.onNodeMouseLeave);

  const mouseClientXRef = useRef<number | undefined>(undefined);
  const mouseClientYRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    setupWebGL({canvasRef, nodeFillProgramRef, nodeStrokeProgramRef, lineProgramRef, glInfoRef});
    const {current: glInfo} = glInfoRef;
    if (glInfo !== undefined) {
      const {gl} = glInfo;
      resizeViewport(gl, true);
    }
    return () => {
      const {current: innerGLInfo} = glInfoRef;
      const {current: status} = statusRef;
      if (innerGLInfo !== undefined) {
        if (status.status === AnimationStatus.InProgress) {
          const {timeline} = status;
          (timeline as any).stop();
        }
        const {gl, nodeFillsBuffer} = innerGLInfo;
        innerGLInfo.nodeFillsBuffer = null as any;
        gl.deleteBuffer(nodeFillsBuffer);
      }
    };
  }, []);

  usePropsChangeRateLimiter<PropsChangeHandlerExtraInputs, MonitoredProps>({
    value: {
      nodes: props.nodes,
      width: props.width,
      height: props.height,
      lines: props.lines,
      hoveredNode: props.hoveredNode,
    },
    getExtraInputToPropsChangeHandler: () => ({
      glInfoRef, nodeFillProgramRef, nodeStrokeProgramRef,  lineProgramRef,
      statusRef, transformationMatrixRef,
      shortDashLength: props.shortDashLength,
      longDashLength: props.longDashLength,
      lineColor: props.lineColor,
      unhoveredNodeColor: props.unhoveredNodeColor,
      dataSetTransitionDuration: props.dataSetTransitionDuration,
      hoverTransitionDuration: props.hoverTransitionDuration,
      longDashColor: props.longDashColor,
      rootElRef,
      maxNumLabels: props.maxNumLabels,
    }),
    performPropsChange,
  });

  const unthrottledHoverHandler = getUnthrottledHoverHandler({
    statusRef, canvasRef,
    widthRef, heightRef, onNodeMouseEnterRef, onNodeMouseLeaveRef,
    transformationMatrixRef, mouseClientXRef, mouseClientYRef,
  });

  const throttledPerformHoverRef = useRef(
    throttle(unthrottledHoverHandler, millisecondsPerSeconds / hoverContinuousHitTestFrameRate),
  );

  const onMouseLeave = () => {
    const {current: throttledPerformHover} = throttledPerformHoverRef;
    throttledPerformHover.cancel();
    props.onNodeMouseLeave();
  };

  const onMouseMove = ({clientX, clientY}: React.MouseEvent<any>) => {
    mouseClientXRef.current = clientX;
    mouseClientYRef.current = clientY;
    const {current: throttledPerformHover} = throttledPerformHoverRef;
    throttledPerformHover();
  };

  return (
    <Root
      ref={rootElRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{width: props.width, height: props.height}}
    >
      <canvas
        ref={canvasRef}
        style={{width: props.width, height: props.height}}
      />
    </Root>
  );
};
